/**
 * pricing.ts — the DETERMINISTIC tint menu + vehicle→pricing-class resolution.
 *
 * Every number here was extracted from 11_recommendation-matrix.md and verified
 * line-by-line against 02_source-of-truth-checklist.md §§2–5 (the two files
 * agree on every figure; conflicts would resolve to file 02 per GUI v2 req G.8).
 * The UI ONLY ever renders prices from this table — the AI never generates or
 * estimates a price (req 5.2). If a combination has no entry here, the panel
 * says so explicitly.
 *
 * ⚠️ Awaiting Phil's line-by-line verification before the pricing panel ships
 * (req G.8) — see docs/LIVE_COACH_GUI2_2026-06-11.md § Pricing extract.
 *
 * PPF + ceramic coating have NO package prices (FRAMEWORK SLOT, 30_tbd-register.md
 * items 6–7): the panel renders "pending" and the close is the consult/inspection
 * (req G.6) — never an invented number.
 */
import type { BodyStyle, PricingClass, ReasonKey, VehicleState } from "@/types";

export type TierKey = "black" | "black_ceramic" | "i3" | "i3_plus";

export interface TierDef {
  key: TierKey;
  /** Menu name (02 §2). */
  name: string;
  /** Basic / Good / Better / Best ladder position (02 §2). */
  ladder: "Basic" | "Good" | "Better" | "Best";
  /** Full sides + rear glass package price, cents, by pricing class (02 §2). */
  fullPackageCents: Record<PricingClass, number>;
  /** Front-two-windows standalone price, cents (02 §3). */
  frontTwoCents: number;
  /** Spec line — exact "up to X% depending on shade" framing (02 §2, 26 §2). */
  tser: string;
  /** IRR is flat within each line and may be stated per line (02 §2). */
  irr: string;
  /** Life Happens tier coverage (02 §9). */
  lifeHappens: "one-time no-fault" | "unlimited lifetime no-fault";
}

/** Source: 02_source-of-truth-checklist.md §2 (verified vs 11_recommendation-matrix.md). */
export const TIERS: TierDef[] = [
  {
    key: "black",
    name: "autobahn Black",
    ladder: "Basic",
    fullPackageCents: { car_truck: 34900, van_suv: 41900 },
    frontTwoCents: 19900,
    tser: "TSER up to 43% depending on shade",
    irr: "IRR 13%",
    lifeHappens: "one-time no-fault",
  },
  {
    key: "black_ceramic",
    name: "autobahn Black Ceramic",
    ladder: "Good",
    fullPackageCents: { car_truck: 50900, van_suv: 57900 },
    frontTwoCents: 24900,
    tser: "TSER up to 63% depending on shade",
    irr: "IRR 60%",
    lifeHappens: "one-time no-fault",
  },
  {
    key: "i3",
    name: "autobahn i3",
    ladder: "Better",
    fullPackageCents: { car_truck: 74900, van_suv: 89900 },
    frontTwoCents: 32900,
    tser: "TSER up to 71% depending on shade",
    irr: "IRR 93%",
    lifeHappens: "unlimited lifetime no-fault",
  },
  {
    key: "i3_plus",
    name: "autobahn i3+",
    ladder: "Best",
    fullPackageCents: { car_truck: 89900, van_suv: 107900 },
    frontTwoCents: 39500,
    tser: "TSER 62–74% depending on shade",
    irr: "IRR 99%",
    lifeHappens: "unlimited lifetime no-fault",
  },
];

/** All lines block more than 99% of UV (02 §2). */
export const UV_LINE = "All lines block over 99% of UV";

/** Windshield / brow / removal (02 §4). */
export const ADD_ONS = {
  windshieldAirCeramic: {
    label: "Windshield — AirCeramic / Black Ceramic",
    cents: 29900,
    note: "AirCeramic: 70–80% VLT, heat rejection with minimal darkening",
  },
  windshieldI3: { label: "Windshield — i3", cents: 39900, note: null },
  brow: { label: "Brow (AS-1 strip)", cents: 6900, note: null },
  removal: {
    label: "Tint removal",
    cents: 12500,
    note: "$125/hr quoted up front — time confirmed once removal starts",
    perHour: true,
  },
} as const;

/** Deposits / prepayment (02 §5). */
export const DEPOSIT_CENTS = 10000;
export const PREPAY_THRESHOLD_CENTS = 100000;

export function formatCents(cents: number): string {
  const dollars = cents / 100;
  return Number.isInteger(dollars) ? `$${dollars.toLocaleString()}` : `$${dollars.toFixed(2)}`;
}

/* ── Vehicle resolution (req 1.6) — deterministic, never model-generated ──── */

/** Body style → tint pricing class (02 §2: Car/Truck vs Van/SUV). */
export const BODY_TO_CLASS: Record<BodyStyle, PricingClass> = {
  sedan: "car_truck",
  coupe: "car_truck",
  hatchback: "car_truck",
  convertible: "car_truck",
  wagon: "car_truck",
  truck: "car_truck",
  suv: "van_suv",
  crossover: "van_suv",
  van: "van_suv",
  minivan: "van_suv",
};

export const CLASS_LABEL: Record<PricingClass, string> = {
  car_truck: "Car / Truck",
  van_suv: "Van / SUV",
};

/* ── Recommendation anchor (req 5.4 / A.10) ─────────────────────────────────
 * Owner policy June 10, 2026 (11_recommendation-matrix.md): the standard
 * starting recommendation is Black Ceramic, reached through the ceramic
 * education moment. Any heat concern keeps the floor at Black Ceramic with i3
 * as the meaningful step up; high-heat / premium / parks-outside / asks-best
 * earns i3 or i3+. autobahn Black stays visible in the table but is NEVER the
 * anchor — it is the respectful fallback for the firmly price-bound customer.
 */
export interface Recommendation {
  anchor: TierKey;
  stepUp: TierKey | null;
  /** One-line reason tied to the signals (11_recommendation-matrix.md table). */
  reason: string;
}

export function recommendTier(
  reasons: ReasonKey[],
  signals: { asksForBest?: boolean; highHeat?: boolean } = {},
): Recommendation {
  if (signals.asksForBest) {
    return {
      anchor: "i3_plus",
      stepUp: null,
      reason: "Asked for the best — i3+ is the maximum, unlimited lifetime Life Happens Warranty",
    };
  }
  // High heat sensitivity / premium / parks outside / long commute → i3 (11 row 3).
  if (signals.highHeat) {
    return {
      anchor: "i3",
      stepUp: "i3_plus",
      reason: "High heat sensitivity — i3 (93% IRR), i3+ positioned as the maximum",
    };
  }
  // ANY heat concern keeps the floor at Black Ceramic with i3 the step up (11 row 2).
  if (reasons.includes("heat") || reasons.includes("fuel_costs")) {
    return {
      anchor: "black_ceramic",
      stepUp: "i3",
      reason: "Heat mentioned — Black Ceramic is the floor, i3 is the meaningful step up",
    };
  }
  return {
    anchor: "black_ceramic",
    stepUp: "i3",
    reason: "Standard start after the ceramic education — Black Ceramic, with i3 as the step up",
  };
}

export function tier(key: TierKey): TierDef {
  const found = TIERS.find((t) => t.key === key);
  if (!found) throw new Error(`unknown tier ${key}`);
  return found;
}

/** One-line quote summary the advisor can read aloud (req 5.7). */
export function quoteSummary(vehicle: VehicleState, tierKey: TierKey): string | null {
  const cls = vehicle.pricingClass;
  if (!cls) return null;
  const t = tier(tierKey);
  const name = [vehicle.year.value, vehicle.make.value, vehicle.model.value]
    .filter((v) => v !== null)
    .join(" ");
  const price = formatCents(t.fullPackageCents[cls]);
  return `${name || "Vehicle"}, ${t.name}, full sides and rear: ${price}`;
}
